import { useState, useEffect } from 'react';
import { Hash, Address } from 'viem';
import { SupportedChainId, SupportedChains } from '@packet/constants/AppSupportChains';
import { tokenPoolCache } from '@/utils/tokenPool';
import { ChainConfigMap } from '@/const/chains';

interface TokenInfo {
  symbol: string;
  name: string;
  decimals: number;
}

interface TokenInfoMap {
  [assetId: string]: {
    symbol: string;
    name: string;
    decimals: number;
    address: Address;
    loading: boolean;
    error: string | null;
  };
}

interface UseMultipleTokenInfoResult {
  tokenInfoMap: TokenInfoMap;
  allLoading: boolean;
  hasErrors: boolean;
}

/**
 * 用于批量获取多个 token 信息的 hook
 */
export const useMultipleTokenInfo = (assetIds: Hash[], chainId: SupportedChainId): UseMultipleTokenInfoResult => {
  const [tokenInfoMap, setTokenInfoMap] = useState<TokenInfoMap>({});
  const [allLoading, setAllLoading] = useState(true);
  const [hasErrors, setHasErrors] = useState(false);

  // 使用 JSON.stringify 来稳定化数组依赖
  const assetIdsKey = JSON.stringify(assetIds);

  useEffect(() => {
    let isMounted = true;

    const fetchAllTokenInfo = async () => {
      if (assetIds.length === 0) {
        if (isMounted) {
          setTokenInfoMap({});
          setAllLoading(false);
          setHasErrors(false);
        }
        return;
      }

      setAllLoading(true);
      setHasErrors(false);

      // 找到对应的链配置
      const chain = ChainConfigMap[chainId];
      if (!chain || !chain.appContract) {
        if (isMounted) {
          const errorMap: TokenInfoMap = {};
          assetIds.forEach((assetId) => {
            errorMap[assetId] = {
              symbol: assetId,
              name: assetId,
              address: assetId as Address,
              decimals: 18,
              loading: false,
              error: `Unsupported chainId: ${chainId} or missing app contract`,
            };
          });
          setTokenInfoMap(errorMap);
          setAllLoading(false);
          setHasErrors(true);
        }
        return;
      }

      const contractAddress = chain.appContract as Address;
      const newTokenInfoMap: TokenInfoMap = {};

      // 并行获取所有 token 信息
      const promises = assetIds.map(async (assetId) => {
        try {
          // 通过 tokenPoolCache 获取pool信息
          const poolInfo = await tokenPoolCache.getPool(contractAddress, chainId, assetId);
          const tokenAddress = poolInfo.token;

          // 通过 tokenPoolCache 获取token完整信息
          const info = await tokenPoolCache.getTokenInfo(tokenAddress, chainId);

          return {
            assetId,
            tokenInfo: info,
            poolInfo,
            error: null,
          };
        } catch (err) {
          console.error(`Failed to fetch token info for ${assetId}:`, err);
          return {
            assetId,
            tokenInfo: null,
            error: err instanceof Error ? err.message : 'Unknown error',
          };
        }
      });

      try {
        const results = await Promise.all(promises);

        if (isMounted) {
          let hasAnyErrors = false;

          results.forEach(({ assetId, tokenInfo, error, poolInfo }) => {
            newTokenInfoMap[assetId] = {
              symbol: tokenInfo?.symbol || assetId,
              name: tokenInfo?.name || assetId,
              decimals: tokenInfo?.decimals || 18,
              address: poolInfo?.token || (assetId as Address),
              loading: false,
              error,
            };

            if (error) {
              hasAnyErrors = true;
            }
          });

          setTokenInfoMap(newTokenInfoMap);
          setAllLoading(false);
          setHasErrors(hasAnyErrors);
        }
      } catch (err) {
        if (isMounted) {
          console.error('Failed to fetch token info:', err);
          const errorMap: TokenInfoMap = {};
          assetIds.forEach((assetId) => {
            errorMap[assetId] = {
              symbol: assetId,
              name: assetId,
              address: assetId as Address,
              decimals: 18,
              loading: false,
              error: err instanceof Error ? err.message : 'Unknown error',
            };
          });
          setTokenInfoMap(errorMap);
          setAllLoading(false);
          setHasErrors(true);
        }
      }
    };

    fetchAllTokenInfo();

    return () => {
      isMounted = false;
    };
  }, [assetIdsKey, chainId]);

  return {
    tokenInfoMap,
    allLoading,
    hasErrors,
  };
};
